<template>
  <div style="display: flex; flex-direction: column; gap: 6px; width: 100%">
    <SelectButton
      v-if="showTabs"
      v-model="drugSearchFilter"
      :options="searchTypes"
      optionLabel="name"
      optionValue="value"
      style="display: flex"
    />
    <Dropdown
      v-if="!showTabs || drugSearchFilter !== 3"
      v-model="searchedDrugName"
      ref="drugSearchDropdown"
      :options="formattedDrugList"
      optionLabel="full_name"
      :loading="loadingDrugs"
      :editable="true"
      placeholder="Search for a drug"
      style="width: 100%"
      @input="onInput"
      @change="onChange(searchedDrugName)"
    >
      <template #option="slotProps">
        <div>
          <b v-if="slotProps.option.generic_name"
            >({{ slotProps.option.generic_name }})</b
          >
          <span v-if="slotProps.option.name !== slotProps.option.generic_name">
            <span v-if="slotProps.option.generic_name"> &ndash; </span>
            <i>{{ slotProps.option.name }}</i>
          </span>
        </div>
      </template>
    </Dropdown>
    <input
      v-else-if="showTabs"
      type="text"
      :value="modelValue"
      @input="onCustomInput($event.target.value)"
      placeholder="Enter drug/substance name"
    />
  </div>
</template>

<script>
import { searchIndex } from "../services/algoliaSearch";
import { sortMedicationList } from "../services/SortMedicationList";
import { debounceSearch } from "../services/debounce";

export default {
  props: {
    modelValue: { type: String, default: "" },
    aliasValue: { type: String, default: null },
    showTabs: { type: Boolean, default: true },
  },
  emits: ["update:modelValue", "update:aliasValue"],
  data() {
    return {
      searchedDrugName: this.modelValue,
      drugList: [],
      loadingDrugs: false,
      drugSearchFilter: 0,
      // Incremented on every search fired; lets in-flight requests detect they've been
      // superseded by a newer query and discard their (possibly out-of-order) response.
      searchToken: 0,
      searchTypes: [
        { name: "All", value: 0 },
        { name: "Brands", value: 2 },
        { name: "Active Ingredients", value: 1 },
        { name: "Custom", value: 3 },
      ],
    };
  },
  watch: {
    modelValue(newVal) {
      if (newVal !== this.searchedDrugName) {
        this.searchedDrugName = newVal;
      }
    },
    drugSearchFilter() {
      this.drugList = [];
      // Re-run the search under the new filter instead of just clearing results - the user
      // already typed a query, so switching tabs (All/Brands/Active Ingredients) shouldn't
      // force them to retype it.
      if (this.searchedDrugName) {
        this.algoliaDrugSearch(this.searchedDrugName);
      }
    },
  },
  created() {
    // DrugSearchInput is rendered once per row in several v-for lists (e.g. the ADR editor
    // in EditPatientComponent.vue). A `debounceSearch(...)` created in `methods` is only
    // evaluated once for the whole component definition, so every instance would share the
    // same timer/pending-call state - typing in one row would cancel/steal another row's
    // in-flight search. Creating it here in created() gives each instance its own debounce
    // closure instead.
    this.debouncedDrugSearch = debounceSearch((searchedDrugName) =>
      this.algoliaDrugSearch(searchedDrugName),
    );
  },
  computed: {
    formattedDrugList() {
      return this.drugList.map((drug) => {
        const genericName = drug.generic_name || "";
        const brandName = drug.name || "";
        const formattedName = brandName
          ? brandName.charAt(0).toUpperCase() + brandName.slice(1)
          : "";
        const fullName = genericName
          ? `(${genericName}) – ${formattedName}`
          : formattedName;
        return {
          ...drug,
          generic_name: genericName,
          name: formattedName,
          full_name: fullName,
        };
      });
    },
  },
  methods: {
    onCustomInput(value) {
      this.searchedDrugName = value;
      this.$emit("update:modelValue", value);
      this.$emit("update:aliasValue", null);
    },
    onInput() {
      this.$emit("update:modelValue", this.searchedDrugName);
      this.$emit("update:aliasValue", null);
      this.debouncedDrugSearch(this.searchedDrugName);
    },
    algoliaDrugSearch(searchedDrugName) {
      this.drugList = [];
      if (!searchedDrugName) return;
      // Filter 3 (Custom) uses a plain text input instead of this Algolia-backed dropdown -
      // see the template - so there's no query to run for it here.
      if (![0, 1, 2].includes(this.drugSearchFilter)) return;

      // Capture this search's token so its response can detect being superseded by a
      // newer, faster-returning search before it resolves.
      const requestToken = ++this.searchToken;

      // Filter 1 = Active Ingredients (generics only), Filter 2 = Brands (non-generics only),
      // Filter 0 = All (no facet filter). encodeURIComponent is required here - a query
      // containing `&` or `+` (or other reserved/plus characters) would otherwise corrupt
      // this params string (e.g. injecting bogus extra query-string keys).
      let params = `typoTolerance=false&query=${encodeURIComponent(searchedDrugName)}`;
      if (this.drugSearchFilter === 1 || this.drugSearchFilter === 2) {
        const isGeneric = this.drugSearchFilter === 1;
        params += `&facetFilters=is_generic:${isGeneric}`;
      }

      this.loadingDrugs = true;
      searchIndex(
        this.$store.state,
        `${this.$store.state.subdomain}_drugs`,
        params,
      )
        .then((res) => {
          if (requestToken !== this.searchToken) return;
          this.drugList = sortMedicationList(res.data.hits);
        })
        .catch(() => {
          // Nothing to recover here - loadingDrugs is always cleared below.
        })
        .finally(() => {
          if (requestToken !== this.searchToken) return;
          this.loadingDrugs = false;
        });
    },
    onChange(value) {
      if (value && typeof value === "object") {
        this.searchedDrugName = value.full_name;
        this.$emit("update:modelValue", value.full_name);
        this.$emit("update:aliasValue", value.alias_id);
        this.drugList = [];
        if (this.$refs.drugSearchDropdown) {
          this.$refs.drugSearchDropdown.hide();
        }
      } else {
        this.$emit("update:modelValue", value || "");
        this.$emit("update:aliasValue", null);
      }
    },
  },
};
</script>
